/**
 * Money is an integer in the currency's minor unit, everywhere, with no
 * exceptions.
 *
 * The reason is the demo: 0.1 + 0.2 in floating point is 0.30000000000000004,
 * and an academy owner who sees B/. 34.99999 on a phone stops listening to
 * the rest of the pitch. Integers are exact, they survive the database's
 * INTEGER columns unchanged, and the only place a decimal separator exists
 * is the string a human reads.
 *
 * «Cents» is what every field and column is called, and for a balboa that is
 * what it is. For a peso, which has no minor unit anybody writes, the integer
 * IS the peso — `Country.decimals` (locale.ts) says which, and THIS FILE is
 * the only one allowed to scale by it. Anything else that divides by 100 by
 * hand is a Colombian amount shown a hundred times too small.
 */
import { getLocale } from "./locale.js";
/**
 * Groups thousands the way the country writes them: 1,245.50 in Panama,
 * 1.245,50 in Venezuela. Exported because Tendencias writes bare amounts
 * over its bars -- no symbol, no decimals -- and «1240» over a column is a
 * number nobody reads at a glance.
 */
export function groupThousands(whole, locale) {
    return whole.replaceAll(/\B(?=(\d{3})+(?!\d))/g, locale.thousandsSeparator);
}
/** 100 for two decimals, 1 for none: what an integer amount is divided by to read it. */
function unitOf(locale) {
    return 10 ** locale.decimals;
}
/**
 * `3500` → `"B/. 35.00"`; in Colombia `45000` → `"$ 45.000"`. Always every
 * decimal the currency has: this is the form for anything that names an
 * exact obligation or payment.
 */
export function formatMoney(cents, locale = getLocale()) {
    const negative = cents < 0;
    const abs = Math.abs(Math.round(cents));
    const unit = unitOf(locale);
    const whole = groupThousands(String(Math.floor(abs / unit)), locale);
    const sign = negative ? "-" : "";
    if (locale.decimals === 0)
        return `${sign}${locale.currencySymbol} ${whole}`;
    const fraction = String(abs % unit).padStart(locale.decimals, "0");
    return `${sign}${locale.currencySymbol} ${whole}${locale.decimalSeparator}${fraction}`;
}
/**
 * `345000` → `"B/. 3,450"`, `124550` → `"B/. 1,245.50"`.
 *
 * For the headline numbers on Inicio, where two zeros after the point are
 * noise competing with the digits that matter.
 */
export function formatMoneyShort(cents, locale = getLocale()) {
    const formatted = formatMoney(cents, locale);
    if (locale.decimals === 0)
        return formatted;
    const zeros = `${locale.decimalSeparator}${"0".repeat(locale.decimals)}`;
    return formatted.endsWith(zeros) ? formatted.slice(0, -zeros.length) : formatted;
}
/**
 * Strips the locale's currency symbol from a string, explicitly, as itself —
 * before anything else touches it. `B/.` carries its own dot, and a blanket
 * "keep only digits and separators" pass would leave that dot behind for
 * the next step to mistake for one someone typed — see `cleanAmountDigits`
 * on what that costs.
 */
function stripCurrencySymbol(text, locale) {
    return text.split(locale.currencySymbol).join("");
}
/**
 * Digits and the two separators only, normalised to a dot for the decimals,
 * and a leading separator anchored to zero rather than dropped — the cleanup
 * `parseAmountToCents` and `sanitizeAmountText` both start from, so a string
 * like ".50" or "B/. 12.25" MEANS the same thing to either one. Neither
 * function's own job — rejecting ambiguity, or shaping a live keystroke — is
 * decided here; this only strips what is never part of a number.
 *
 * A CURRENCY WITH NO DECIMALS HAS NO DECIMAL SEPARATOR: in pesos every dot
 * or comma is a thousands mark somebody typed or pasted («45.000»), so all of
 * them go and the digits are the amount. Reading that dot as a decimal turns
 * forty-five thousand pesos into forty-five.
 *
 * WITH BOTH SEPARATORS PRESENT, THE LAST ONE IS THE DECIMAL: «1.250,50» and
 * «1,250.50» both read as twelve hundred and fifty and a half, whichever
 * country's keyboard typed them. With one kind only, it is the decimal — a
 * comma is a decimal separator on a Latin American keyboard often enough to
 * be worth reading that way, and thousands separators do not appear in a
 * field this small.
 *
 * A separator with nothing before it means "0.", not "the same as if it
 * were not there". Stripping a leading separator instead of anchoring it to
 * zero turns ".50" — fifty centavos — into fifty balboas. It is only the
 * same-looking character as the stray dot `B/.` leaves behind once its own
 * symbol is stripped, never the same meaning.
 */
function cleanAmountDigits(text, locale) {
    const kept = stripCurrencySymbol(text, locale).replace(/[^\d.,]/g, "");
    if (locale.decimals === 0)
        return kept.replace(/[.,]/g, "");
    const lastDot = kept.lastIndexOf(".");
    const lastComma = kept.lastIndexOf(",");
    const folded = lastDot !== -1 && lastComma !== -1
        ? lastComma > lastDot
            ? kept.replaceAll(".", "").replaceAll(",", ".")
            : kept.replaceAll(",", "")
        : kept.replaceAll(",", ".");
    return folded.startsWith(".") ? `0${folded}` : folded;
}
/**
 * What a person is allowed to have typed into an amount field, applied on
 * every keystroke so a bad character never lands rather than being caught
 * afterwards: a field must never hold «56....23» with nothing stopping it.
 * `MoneyInput` (web and mobile both) calls this in its `onChange`, so
 * whatever `56....23` was on the way to typing collapses to `56.23` the
 * moment the second separator lands, never sitting on screen even for one
 * render.
 *
 * COLLAPSES a second separator rather than refusing the keystroke outright
 * — a live field cannot "reject" a character the way a final validation
 * refuses a whole value; it can only decide what survives, and dropping the
 * extra separator is what keeps typing moving. `parseAmountToCents` makes
 * the opposite call for the same shape of string arriving whole (see its
 * own comment): a value that was never built one keystroke at a time has no
 * "what the person meant a moment ago" to fall back on, so two separators
 * there is refused rather than guessed at.
 *
 * At most two decimal digits survive; a third is DROPPED, never rounded —
 * the number on screen while someone is still typing has to be the number
 * that gets saved, and rounding "35.999" up to "36.00" mid-keystroke would
 * add money nobody asked to add. (`parseAmountToCents` still rounds a value
 * that arrives already at three decimals from somewhere other than typing —
 * see its own comment on why the two rules meet in different places on
 * purpose.)
 */
export function sanitizeAmountText(text, locale = getLocale()) {
    const cleaned = cleanAmountDigits(text, locale);
    const [wholePart = "", ...rest] = cleaned.split(".");
    if (rest.length === 0)
        return wholePart;
    // `rest` joined back is every digit that followed ANY separator -- the
    // second one on, collapsed into the first, is exactly what stops
    // "56....23" from growing a separator per keystroke. Written back with the
    // country's OWN separator: a Venezuelan field shows «12,5» while typing.
    return `${wholePart}${locale.decimalSeparator}${rest.join("").slice(0, locale.decimals)}`;
}
/**
 * Reads what someone typed into an amount field.
 *
 * Accepts "35", "35.5", "35,50" and "B/. 35.00" — a comma is a decimal
 * separator on a Latin American keyboard often enough to be worth handling,
 * and thousands separators do not appear in a field this small. Returns null
 * for anything that is not a positive amount, which is what the caller shows
 * a validation message for.
 *
 * REFUSES two separators rather than collapsing them, unlike
 * `sanitizeAmountText` above — see that function's own comment on why a
 * value arriving whole gets the opposite call from one built a keystroke at
 * a time.
 */
export function parseAmountToCents(input, locale = getLocale()) {
    // The minus sign has to be caught BEFORE stripping, or "-5" quietly becomes
    // a payment of B/. 5.00.
    if (input.trim().startsWith("-"))
        return null;
    const cleaned = cleanAmountDigits(input, locale);
    if (cleaned === "")
        return null;
    if ((cleaned.match(/\./g) ?? []).length > 1)
        return null;
    const value = Number(cleaned);
    if (!Number.isFinite(value) || value <= 0)
        return null;
    const unit = unitOf(locale);
    // Rounding rather than truncating: a value that arrives ALREADY at three
    // decimals — never typed, since `sanitizeAmountText` refuses a third
    // decimal digit at the keyboard, but still possible from a source this
    // function does not control — becomes 3600 cents from "35.999", not 3599.
    // The two rules meet in different places ON PURPOSE: typing truncates
    // (`sanitizeAmountText`, above), a value handed in whole rounds (here).
    return Math.round(value * unit);
}
/**
 * A LINE'S PRICE, WHERE 0 IS A PRICE: a box saying 0 must not leave the line
 * at its old 2.00. `parseAmountToCents` refuses a zero
 * on purpose -- a payment of nothing is no payment -- but a line may be free,
 * and the server takes it. Anything else reads exactly as an amount.
 */
export function parsePriceToCents(input, locale = getLocale()) {
    const amount = parseAmountToCents(input, locale);
    if (amount !== null || input.trim().startsWith("-"))
        return amount;
    const cleaned = cleanAmountDigits(input, locale);
    // A digit TYPED, not the «0» `cleanAmountDigits` puts before a bare «.».
    return /\d/.test(input) && /^0*\.?0*$/.test(cleaned) ? 0 : null;
}
/** The amount as a number, for what a machine reads (schema.org's `minPrice`): `3550` → 35.5, `45000` → 45000 in pesos. */
export function centsToAmount(cents, locale = getLocale()) {
    return cents / unitOf(locale);
}
/** The editable form of an amount: `3500` → `"35.00"`, `125050` → `"1250,50"` in bolívares, `45000` → `"45000"` in pesos. No symbol, no groups. */
export function centsToInput(cents, locale = getLocale()) {
    return (cents / unitOf(locale)).toFixed(locale.decimals).replace(".", locale.decimalSeparator);
}
/**
 * A PRICE RAISED OR LOWERED, as VozIA proposes it. A percentage lands on a
 * price a shop would write: the nearest 5 cents (half up) where the currency
 * has cents, the nearest unit where it has none. A fixed amount is added as
 * given. Null when the result is zero or less: such a price is not proposed.
 */
export function bumpPriceCents(priceCents, change, locale) {
    let result;
    if ("deltaCents" in change) {
        result = priceCents + change.deltaCents;
    }
    else {
        // toFixed first: 735 * 1.1 is 808.4999… in floating point, not 808.5.
        const raw = Number((priceCents * (1 + change.percent / 100)).toFixed(6));
        result = locale.decimals === 2 ? Math.round(raw / 5) * 5 : Math.round(raw);
    }
    return result > 0 ? result : null;
}
